import { useState } from "react";
import { DataTable } from "../components/DataTable";
import { useAuth } from "../context/AuthContext";

type CartStatus = "Active" | "Abandoned" | "Converted" | "Expired";

type CartItem = {
  productName: string;
  sku: string;
  qty: number;
  price: number;
};

type Cart = {
  id: string;
  cartRef: string;
  customer: string;
  customerEmail: string;
  phone: string;
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  discount: number;
  couponCode: string;
  shippingCharge: number;
  total: number;
  status: CartStatus;
  createdAt: string;
  updatedAt: string;
  ipAddress: string;
  device: string;
};

const sampleCarts: Cart[] = [
  {
    id: "cart1", cartRef: "CART-20240501-001",
    customer: "Priya Sharma", customerEmail: "priya@example.com", phone: "98765-43210",
    items: [
      { productName: "Gold Bangles Set", sku: "GLD-BNG-001", qty: 1, price: 12500 },
      { productName: "Diamond Earrings", sku: "DMD-EAR-003", qty: 1, price: 8200 },
    ],
    itemCount: 2, subtotal: 20700, discount: 2070, couponCode: "WELCOME10",
    shippingCharge: 0, total: 18630,
    status: "Active", createdAt: "2024-05-01 10:32", updatedAt: "2024-05-01 11:15",
    ipAddress: "103.25.88.12", device: "Mobile",
  },
  {
    id: "cart2", cartRef: "CART-20240430-045",
    customer: "Ananya Reddy", customerEmail: "ananya@example.com", phone: "87654-32109",
    items: [
      { productName: "Bridal Necklace Set", sku: "BRL-NCK-007", qty: 1, price: 45000 },
    ],
    itemCount: 1, subtotal: 45000, discount: 500, couponCode: "BRIDE500",
    shippingCharge: 0, total: 44500,
    status: "Abandoned", createdAt: "2024-04-30 14:20", updatedAt: "2024-04-30 16:45",
    ipAddress: "49.36.102.5", device: "Desktop",
  },
  {
    id: "cart3", cartRef: "CART-20240429-089",
    customer: "Sneha Verma", customerEmail: "sneha@example.com", phone: "76543-21098",
    items: [
      { productName: "Silver Anklets Pair", sku: "SLV-ANK-012", qty: 2, price: 1800 },
      { productName: "Gold Nose Pin", sku: "GLD-NSP-004", qty: 1, price: 2200 },
    ],
    itemCount: 3, subtotal: 5800, discount: 0, couponCode: "",
    shippingCharge: 79, total: 5879,
    status: "Converted", createdAt: "2024-04-29 09:10", updatedAt: "2024-04-29 09:42",
    ipAddress: "117.55.66.99", device: "Mobile",
  },
  {
    id: "cart4", cartRef: "CART-20240428-012",
    customer: "Meena Pillai", customerEmail: "meena@example.com", phone: "65432-10987",
    items: [
      { productName: "Kundan Jhumkas", sku: "KND-JHM-019", qty: 1, price: 3400 },
    ],
    itemCount: 1, subtotal: 3400, discount: 340, couponCode: "DIWALI25",
    shippingCharge: 49, total: 3109,
    status: "Expired", createdAt: "2024-04-28 18:05", updatedAt: "2024-04-28 20:00",
    ipAddress: "182.74.33.21", device: "Tablet",
  },
  {
    id: "cart5", cartRef: "CART-20240501-067",
    customer: "Ritu Agarwal", customerEmail: "ritu@example.com", phone: "54321-09876",
    items: [
      { productName: "Pearl Mala", sku: "PRL-MLA-002", qty: 1, price: 6800 },
      { productName: "Gold Chain 22K", sku: "GLD-CHN-011", qty: 1, price: 15200 },
      { productName: "Ruby Ring", sku: "RBY-RNG-008", qty: 1, price: 9500 },
    ],
    itemCount: 3, subtotal: 31500, discount: 0, couponCode: "",
    shippingCharge: 0, total: 31500,
    status: "Active", createdAt: "2024-05-01 08:00", updatedAt: "2024-05-01 12:30",
    ipAddress: "59.184.220.7", device: "Desktop",
  },
];

const STATUS_COLORS: Record<CartStatus, string> = {
  Active: "text-bg-success",
  Abandoned: "text-bg-warning",
  Converted: "text-bg-primary",
  Expired: "text-bg-secondary",
};

const DEVICE_ICONS: Record<string, string> = {
  Mobile: "📱", Desktop: "🖥️", Tablet: "📟",
};

const CART_STYLES = `
  .ct-detail-overlay { position:fixed; inset:0; background:rgba(26,18,8,.52); backdrop-filter:blur(4px); z-index:1000; display:flex; align-items:flex-start; justify-content:flex-end; padding:0; overflow:hidden; animation:ctFadeIn .2s ease; }
  @keyframes ctFadeIn { from{opacity:0} to{opacity:1} }
  .ct-drawer { background:#fff; width:100%; max-width:480px; height:100vh; overflow-y:auto; box-shadow:-8px 0 40px rgba(26,18,8,.18); animation:ctSlide .25s cubic-bezier(.4,0,.2,1); display:flex; flex-direction:column; }
  @keyframes ctSlide { from{transform:translateX(100%)} to{transform:none} }
  .ct-drawer-head { padding:20px 22px; border-bottom:1.5px solid #f0ebe4; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; background:#fff; z-index:2; }
  .ct-drawer-title { font-size:16px; font-weight:800; color:#1a1208; }
  .ct-drawer-ref { font-size:12px; color:#b09a88; font-weight:500; font-family:monospace; }
  .ct-close { width:34px; height:34px; border:1.5px solid #ede6df; border-radius:9px; background:#fdf8f3; cursor:pointer; font-size:16px; color:#7a6652; display:flex; align-items:center; justify-content:center; }
  .ct-section { padding:18px 22px; border-bottom:1px solid #f5f0eb; }
  .ct-sec-title { font-size:11px; font-weight:800; color:#b09a88; text-transform:uppercase; letter-spacing:.8px; margin-bottom:12px; }
  .ct-row { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px; }
  .ct-key { font-size:12.5px; color:#7a6652; }
  .ct-val { font-size:13px; font-weight:600; color:#1a1208; text-align:right; max-width:60%; }
  .ct-item-card { background:#fdf8f3; border:1px solid #f0ebe4; border-radius:10px; padding:10px 12px; margin-bottom:8px; }
  .ct-item-name { font-size:13px; font-weight:700; color:#1a1208; margin-bottom:3px; }
  .ct-item-meta { font-size:12px; color:#7a6652; display:flex; gap:10px; }
  .ct-summary-row { display:flex; justify-content:space-between; font-size:13px; color:#4a3728; margin-bottom:6px; }
  .ct-summary-total { display:flex; justify-content:space-between; font-size:15px; font-weight:800; color:#1a1208; padding-top:10px; border-top:1.5px solid #f0ebe4; margin-top:6px; }
  .ct-coupon-chip { display:inline-flex; align-items:center; gap:4px; background:var(--theme-light); color:var(--theme); padding:3px 10px; border-radius:20px; font-size:12px; font-weight:700; font-family:monospace; }
  .ct-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:0; }
  .ct-stat { background:#fff; border:1px solid #f0ebe4; border-radius:12px; padding:14px 16px; text-align:center; box-shadow:0 1px 4px rgba(26,18,8,.05); }
  .ct-stat-val { font-size:20px; font-weight:800; color:#1a1208; display:block; }
  .ct-stat-lbl { font-size:11px; color:#b09a88; font-weight:600; text-transform:uppercase; letter-spacing:.5px; }
  .ct-action-btn { padding:8px 14px; border-radius:9px; border:1.5px solid #ede6df; background:#fdf8f3; font-size:12px; font-weight:600; cursor:pointer; color:#7a6652; font-family:inherit; transition:all .15s; }
  .ct-action-btn:hover { border-color:var(--theme); color:var(--theme); background:var(--theme-light); }
  .ct-recover-btn { padding:8px 16px; border-radius:9px; border:none; background:linear-gradient(135deg,#d4551a,var(--theme)); color:#fff; font-size:12px; font-weight:700; cursor:pointer; font-family:inherit; }
`;

export function CartPage() {
  const { user } = useAuth();
  const [carts, setCarts] = useState<Cart[]>(sampleCarts);
  const [selectedCart, setSelectedCart] = useState<Cart | null>(null);
  const [filterStatus, setFilterStatus] = useState<CartStatus | "All">("All");
  const canManage = user?.role === "admin";

  const filteredCarts = filterStatus === "All" ? carts : carts.filter(c => c.status === filterStatus);

  const totalActive = carts.filter(c => c.status === "Active").length;
  const totalAbandoned = carts.filter(c => c.status === "Abandoned").length;
  const totalRevenue = carts.filter(c => c.status === "Converted").reduce((s, c) => s + c.total, 0);
  const totalValue = carts.filter(c => c.status === "Active").reduce((s, c) => s + c.total, 0);

  function deleteCart(id: string) { if (!canManage) return; setCarts(p => p.filter(c => c.id !== id)); }
  function recoverCart(cart: Cart) {
    setCarts(p => p.map(c => c.id === cart.id ? { ...c, status: "Active" } : c));
    setSelectedCart(prev => prev ? { ...prev, status: "Active" } : null);
  }

  return (
    <>
      <style>{CART_STYLES}</style>
      <div className="d-grid gap-3">
        {/* Summary Stats */}
        <div className="ct-stats">
          <div className="ct-stat">
            <span className="ct-stat-val" style={{ color: "#059669" }}>{totalActive}</span>
            <span className="ct-stat-lbl">Active Carts</span>
          </div>
          <div className="ct-stat">
            <span className="ct-stat-val" style={{ color: "#d97706" }}>{totalAbandoned}</span>
            <span className="ct-stat-lbl">Abandoned</span>
          </div>
          <div className="ct-stat">
            <span className="ct-stat-val" style={{ color: "var(--theme)" }}>₹{(totalValue / 1000).toFixed(1)}K</span>
            <span className="ct-stat-lbl">Active Value</span>
          </div>
          <div className="ct-stat">
            <span className="ct-stat-val" style={{ color: "#1a1208" }}>₹{(totalRevenue / 1000).toFixed(1)}K</span>
            <span className="ct-stat-lbl">Converted Rev</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="d-flex gap-2 flex-wrap">
          {(["All", "Active", "Abandoned", "Converted", "Expired"] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterStatus(s)}
              className="btn btn-sm"
              style={{
                borderRadius: 20,
                border: `1.5px solid ${filterStatus === s ? "var(--theme)" : "#ede6df"}`,
                background: filterStatus === s ? "var(--theme-light)" : "#fdf8f3",
                color: filterStatus === s ? "var(--theme)" : "#7a6652",
                fontWeight: 600,
              }}
            >
              {s}
              {s !== "All" && (
                <span className="ms-1" style={{ opacity: .7 }}>
                  ({carts.filter(c => c.status === s).length})
                </span>
              )}
            </button>
          ))}
        </div>

        <DataTable
          title="Cart Management"
          rows={filteredCarts as unknown as Record<string, unknown>[]}
          columns={[
            {
              key: "cartRef", label: "Cart Ref",
              render: (row) => <code style={{ fontSize: 12, color: "var(--theme)", fontWeight: 700 }}>{String(row.cartRef)}</code>,
            },
            {
              key: "customer", label: "Customer",
              render: (row) => (
                <div>
                  <div style={{ fontWeight: 700, color: "#1a1208", fontSize: 13 }}>{String(row.customer)}</div>
                  <div style={{ fontSize: 11, color: "#b09a88" }}>{String(row.customerEmail)}</div>
                </div>
              ),
            },
            {
              key: "itemCount", label: "Items",
              render: (row) => <span className="badge text-bg-secondary">{String(row.itemCount)} items</span>,
            },
            {
              key: "subtotal", label: "Subtotal",
              render: (row) => <span style={{ fontWeight: 600 }}>₹{Number(row.subtotal).toLocaleString()}</span>,
            },
            {
              key: "discount", label: "Discount",
              render: (row) => Number(row.discount) > 0
                ? <span style={{ color: "#059669", fontWeight: 600 }}>-₹{Number(row.discount).toLocaleString()}</span>
                : <span style={{ color: "#c4b5a8" }}>—</span>,
            },
            {
              key: "total", label: "Total",
              render: (row) => <span style={{ fontWeight: 800, color: "#1a1208" }}>₹{Number(row.total).toLocaleString()}</span>,
            },
            {
              key: "device", label: "Device",
              render: (row) => <span>{DEVICE_ICONS[String(row.device)] || "🖥️"} {String(row.device)}</span>,
            },
            {
              key: "status", label: "Status",
              render: (row) => (
                <span className={`badge ${STATUS_COLORS[row.status as CartStatus]}`}>{String(row.status)}</span>
              ),
            },
            {
              key: "createdAt", label: "Created",
              render: (row) => <small style={{ color: "#b09a88" }}>{String(row.createdAt)}</small>,
            },
            {
              key: "actions", label: "Actions", className: "text-end",
              render: (row) => (
                <div className="d-flex gap-2 justify-content-end">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedCart(row as unknown as Cart)}>View</button>
                  {canManage && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteCart(row.id as string)}>Delete</button>
                  )}
                </div>
              ),
            },
          ]}
          rowKey={(row) => String(row.id)}
          searchPlaceholder="Search by customer, ref..."
          exportFileName="carts"
          onDeleteRow={(id) => deleteCart(id as string)}
          deleteItemLabel="Cart"
        />
      </div>

      {/* Cart Detail Drawer */}
      {selectedCart && (
        <div className="ct-detail-overlay" onClick={e => { if (e.target === e.currentTarget) setSelectedCart(null); }}>
          <div className="ct-drawer">
            <div className="ct-drawer-head">
              <div>
                <div className="ct-drawer-title">Cart Details</div>
                <div className="ct-drawer-ref">{selectedCart.cartRef}</div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${STATUS_COLORS[selectedCart.status]}`}>{selectedCart.status}</span>
                <button className="ct-close" onClick={() => setSelectedCart(null)} type="button">✕</button>
              </div>
            </div>

            <div className="ct-section">
              <div className="ct-sec-title">Customer Info</div>
              <div className="ct-row"><span className="ct-key">Name</span><span className="ct-val">{selectedCart.customer}</span></div>
              <div className="ct-row"><span className="ct-key">Email</span><span className="ct-val" style={{ fontSize: 12 }}>{selectedCart.customerEmail}</span></div>
              <div className="ct-row"><span className="ct-key">Phone</span><span className="ct-val">{selectedCart.phone}</span></div>
              <div className="ct-row"><span className="ct-key">IP Address</span><span className="ct-val" style={{ fontFamily: "monospace", fontSize: 12 }}>{selectedCart.ipAddress}</span></div>
              <div className="ct-row"><span className="ct-key">Device</span><span className="ct-val">{DEVICE_ICONS[selectedCart.device]} {selectedCart.device}</span></div>
            </div>

            <div className="ct-section">
              <div className="ct-sec-title">Items ({selectedCart.itemCount})</div>
              {selectedCart.items.map((item, i) => (
                <div key={i} className="ct-item-card">
                  <div className="ct-item-name">{item.productName}</div>
                  <div className="ct-item-meta">
                    <span>SKU: {item.sku}</span>
                    <span>Qty: {item.qty}</span>
                    <span style={{ marginLeft: "auto", fontWeight: 700, color: "#1a1208" }}>₹{(item.price * item.qty).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="ct-section">
              <div className="ct-sec-title">Order Summary</div>
              <div className="ct-summary-row"><span>Subtotal</span><span>₹{selectedCart.subtotal.toLocaleString()}</span></div>
              {selectedCart.discount > 0 && (
                <div className="ct-summary-row">
                  <span>Discount {selectedCart.couponCode && <span className="ct-coupon-chip ms-1">🏷️ {selectedCart.couponCode}</span>}</span>
                  <span style={{ color: "#059669" }}>-₹{selectedCart.discount.toLocaleString()}</span>
                </div>
              )}
              <div className="ct-summary-row">
                <span>Shipping</span>
                <span>{selectedCart.shippingCharge > 0 ? `₹${selectedCart.shippingCharge}` : <span style={{ color: "#059669" }}>FREE</span>}</span>
              </div>
              <div className="ct-summary-total"><span>Total</span><span>₹{selectedCart.total.toLocaleString()}</span></div>
            </div>

            <div className="ct-section">
              <div className="ct-sec-title">Timeline</div>
              <div className="ct-row"><span className="ct-key">Created</span><span className="ct-val">{selectedCart.createdAt}</span></div>
              <div className="ct-row"><span className="ct-key">Last Updated</span><span className="ct-val">{selectedCart.updatedAt}</span></div>
            </div>

            {selectedCart.status === "Abandoned" && canManage && (
              <div className="ct-section d-flex gap-2">
                <button className="ct-action-btn" type="button">📧 Send Recovery Email</button>
                <button className="ct-recover-btn" type="button" onClick={() => recoverCart(selectedCart)}>Mark Active</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
